import {
  type ClientCompany,
  type CreateClientDetailsDto,
  type EntityType,
} from '@nestjs-boilerplate/types';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { type UseFormReturn, useFieldArray } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

const MAX_COMPANIES = 3;

interface CompanyDetailsSectionProps {
  form: UseFormReturn<CreateClientDetailsDto>;
  entityType: EntityType | null | undefined;
}

export function CompanyDetailsSection({ form, entityType }: CompanyDetailsSectionProps) {
  const [expandedCompanies, setExpandedCompanies] = useState<number[]>([]);
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [newCompany, setNewCompany] = useState<Partial<ClientCompany>>({
    companyName: '',
    companyNumber: '',
    dateOfIncorporation: null,
    addressLine1: '',
    addressLine2: '',
    townCity: '',
    county: '',
    postcode: '',
    country: 'GB',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'companies',
  });

  const toggleCompanyExpanded = (index: number) => {
    setExpandedCompanies((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const resetNewCompanyForm = useCallback(() => {
    setNewCompany({
      companyName: '',
      companyNumber: '',
      dateOfIncorporation: null,
      addressLine1: '',
      addressLine2: '',
      townCity: '',
      county: '',
      postcode: '',
      country: 'GB',
    });
  }, []);

  const handleAddCompanyClick = () => {
    setIsAddingCompany(true);
    resetNewCompanyForm();
  };

  const handleSaveCompany = () => {
    if (newCompany.companyName) {
      append({
        companyName: newCompany.companyName,
        companyNumber: newCompany.companyNumber ?? '',
        dateOfIncorporation: newCompany.dateOfIncorporation ?? null,
        addressLine1: newCompany.addressLine1 ?? '',
        addressLine2: newCompany.addressLine2 ?? '',
        townCity: newCompany.townCity ?? '',
        county: newCompany.county ?? '',
        postcode: newCompany.postcode ?? '',
        country: newCompany.country ?? 'GB',
      });
      setIsAddingCompany(false);
      resetNewCompanyForm();
    }
  };

  const updateNewCompanyField = (field: keyof ClientCompany, value: unknown) => {
    setNewCompany((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section className="pb-6">
      <h3 className="mb-2 text-[18px] font-semibold text-[#051923]">Company Details (Optional)</h3>
      <p className="mb-6 text-[14px] text-gray-600">
        {entityType === 'ORGANISATION'
          ? 'Please add the company details below.'
          : 'If the client is a director of a business in the UK or overseas, please add the company details below. If the client has more than one company listed as a source of income, please add each of these businesses.'}
      </p>

      {/* Saved companies as accordions */}
      {fields.length > 0 && (
        <div className="mb-6 space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="rounded-md border border-gray-200">
              {/* Accordion header */}
              <button
                type="button"
                onClick={() => toggleCompanyExpanded(index)}
                className="flex w-full items-center justify-between p-4 hover:bg-gray-50"
              >
                <span className="font-medium text-[#051923]">
                  Company {index + 1}
                  {form.watch(`companies.${index}.companyName`) && (
                    <span className="ml-2 font-normal text-gray-500">
                      - {form.watch(`companies.${index}.companyName`)}
                    </span>
                  )}
                </span>
                {expandedCompanies.includes(index) ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {/* Accordion content - view/edit saved company */}
              {expandedCompanies.includes(index) && (
                <div className="border-t border-gray-200 p-4">
                  <div className="space-y-6">
                    {/* Row 1: Company Name & Date of Incorporation */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name={`companies.${index}.companyName`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`companies.${index}.dateOfIncorporation`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Date of Incorporation</FormLabel>
                            <FormControl>
                              <DatePickerInput
                                value={field.value}
                                onChange={field.onChange}
                                disabled={(date: Date) => date > new Date()}
                                startMonth={new Date(1800, 0)}
                                endMonth={new Date()}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Row 1b: Companies House Number (optional, improves screening accuracy) */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name={`companies.${index}.companyNumber`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Companies House Number (Optional)</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ''}
                                placeholder="e.g. 01234567"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    {/* Row 2: Postcode with Find Address button */}
                    <div className="flex gap-4">
                      <FormField
                        control={form.control}
                        name={`companies.${index}.postcode`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>Postcode</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex items-end">
                        <Button
                          type="button"
                          disabled
                          className="h-10 bg-[#131313] px-6 text-white hover:bg-[#131313]/90"
                        >
                          Find Address
                        </Button>
                      </div>
                    </div>

                    {/* Row 3: Address Line 1 & Address Line 2 */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name={`companies.${index}.addressLine1`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Address Line 1</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`companies.${index}.addressLine2`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Address Line 2 (Optional)</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Row 4: Town/City & County */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name={`companies.${index}.townCity`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Town/City</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`companies.${index}.county`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>County (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="County" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove Company
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Company Form - shown when adding a new company */}
      {isAddingCompany && (
        <div className="mb-6 rounded-md border border-dashed border-gray-300 p-6">
          <h4 className="mb-4 text-[16px] font-semibold text-[#051923]">
            Company {fields.length + 1}
          </h4>

          <div className="space-y-6">
            {/* Row 1: Company Name & Date of Incorporation */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Company Name
                </span>
                <Input
                  value={newCompany.companyName ?? ''}
                  onChange={(e) => updateNewCompanyField('companyName', e.target.value)}
                  placeholder="Enter company name"
                />
              </label>

              <div className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Date of Incorporation
                </span>
                <DatePickerInput
                  value={newCompany.dateOfIncorporation}
                  onChange={(date) => updateNewCompanyField('dateOfIncorporation', date)}
                  disabled={(date: Date) => date > new Date()}
                  startMonth={new Date(1800, 0)}
                  endMonth={new Date()}
                />
              </div>
            </div>

            {/* Row 1b: Companies House Number (optional, improves screening accuracy) */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Companies House Number (Optional)
                </span>
                <Input
                  value={newCompany.companyNumber ?? ''}
                  onChange={(e) => updateNewCompanyField('companyNumber', e.target.value)}
                  placeholder="e.g. 01234567"
                />
              </label>
            </div>

            <Separator />

            {/* Row 2: Postcode with Find Address button */}
            <div className="flex gap-4">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="flex-1 space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Postcode
                </span>
                <Input
                  value={newCompany.postcode ?? ''}
                  onChange={(e) => updateNewCompanyField('postcode', e.target.value)}
                  placeholder="Enter postcode"
                />
              </label>
              <div className="flex items-end">
                <Button
                  type="button"
                  disabled
                  className="h-10 bg-[#131313] px-6 text-white hover:bg-[#131313]/90"
                >
                  Find Address
                </Button>
              </div>
            </div>

            {/* Row 3: Address Line 1 & Address Line 2 */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Address Line 1
                </span>
                <Input
                  value={newCompany.addressLine1 ?? ''}
                  onChange={(e) => updateNewCompanyField('addressLine1', e.target.value)}
                  placeholder="Street address"
                />
              </label>

              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Address Line 2 (Optional)
                </span>
                <Input
                  value={newCompany.addressLine2 ?? ''}
                  onChange={(e) => updateNewCompanyField('addressLine2', e.target.value)}
                  placeholder="Building, floor, etc."
                />
              </label>
            </div>

            {/* Row 4: Town/City & County */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Town/City
                </span>
                <Input
                  value={newCompany.townCity ?? ''}
                  onChange={(e) => updateNewCompanyField('townCity', e.target.value)}
                  placeholder="Town or city"
                />
              </label>

              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  County (Optional)
                </span>
                <Input
                  value={newCompany.county ?? ''}
                  onChange={(e) => updateNewCompanyField('county', e.target.value)}
                  placeholder="County"
                />
              </label>
            </div>

            {/* Row 5: Postcode (read-only, same as above) */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input renders input element */}
              <label className="space-y-2">
                <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Postcode
                </span>
                <Input
                  value={newCompany.postcode ?? ''}
                  onChange={(e) => updateNewCompanyField('postcode', e.target.value)}
                  placeholder="Postcode"
                />
              </label>
            </div>
          </div>

          {/* Save Company button */}
          <div className="mt-6 flex justify-center">
            <Button
              type="button"
              onClick={handleSaveCompany}
              disabled={!newCompany.companyName}
              className="h-[45px] bg-[#131313] px-12 text-[16px] font-medium text-white hover:bg-[#131313]/90"
            >
              Save Company
            </Button>
          </div>
        </div>
      )}

      {/* Add Company button - only show if not at limit and not currently adding */}
      {!isAddingCompany && fields.length < MAX_COMPANIES && (
        <Button
          type="button"
          variant="outline"
          onClick={handleAddCompanyClick}
          className="h-[45px] w-full border-[#131313] bg-[#131313] px-6 text-[16px] font-medium text-white hover:bg-[#131313]/90 hover:text-white"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add a Company
        </Button>
      )}

      {/* Show message when at limit */}
      {fields.length >= MAX_COMPANIES && !isAddingCompany && (
        <p className="text-center text-sm text-gray-500">
          Maximum of {MAX_COMPANIES} companies can be added.
        </p>
      )}
    </section>
  );
}
